export const STATUS_BADGE = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Upcoming: "bg-blue-50 text-blue-700 border-blue-200",
  "Expiring Soon": "bg-amber-50 text-amber-700 border-amber-200",
  Completed: "bg-slate-100 text-slate-600 border-slate-200",
  Cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};

export const STATUS_ORDER = { "Expiring Soon": 0, Active: 1, Upcoming: 2, Completed: 3, Cancelled: 4 };

export const ROWS = 10;
export const BATCH = 50;

export const EMPTY = {
  name: "", client_id: null, client_name: "", client_mobile: "",
  city: "", start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10),
  is_open_ended: false,
  expected_end_date: "",
  end_date_status: "confirmed",
  po_value_status: "confirmed",
  estimated_daily_charge: "",
  estimated_monthly_charge: "",
  estimated_po_value: "",
  confirmed_po_value: "",
  payment_due_date: "",
  gst: { gst_mode: "exclusive", gst_percentage: 18, cgst_percentage: 9, sgst_percentage: 9, igst_percentage: 18, gst_treatment: "cgst_sgst", round_off_behavior: "none" },
  hoarding_ids: [], contract_amounts: {}, notes: "",
};

export function asArray(value) {
  return Array.isArray(value) ? value : [];
}

export function chargeFor(value) {
  if (value && typeof value === "object") {
    return {
      display: Number(value.display || 0),
      printing: Number(value.printing || 0),
      installation: Number(value.installation || 0),
      display_margin_type: value.display_margin_type || "",
      display_margin_value: Number(value.display_margin_value || 0),
      printing_margin_type: value.printing_margin_type || "",
      printing_margin_value: Number(value.printing_margin_value || 0),
      installation_margin_type: value.installation_margin_type || "",
      installation_margin_value: Number(value.installation_margin_value || 0),
    };
  }
  return {
    display: Number(value || 0),
    printing: 0,
    installation: 0,
    display_margin_type: "",
    display_margin_value: 0,
    printing_margin_type: "",
    printing_margin_value: 0,
    installation_margin_type: "",
    installation_margin_value: 0,
  };
}

export function chargeTotal(value) {
  const charge = chargeFor(value);
  return charge.display + charge.printing + charge.installation;
}

function parseDateOnly(value) {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return Date.UTC(year, month - 1, day);
}

export function campaignDurationDays(startDate, endDate, isOpenEnded = false) {
  if (isOpenEnded) return 0;
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (start == null || end == null || end < start) return 0;
  return Math.floor((end - start) / 86400000) + 1;
}

export function campaignChargeTotal(value, startDate, endDate, isOpenEnded = false) {
  const charge = chargeFor(value);
  const days = campaignDurationDays(startDate, endDate, isOpenEnded);
  const displayTotal = days > 0 ? (charge.display * days) / 30 : 0;
  return displayTotal + charge.printing + charge.installation;
}

export function campaignContractTotal(form, hoardings = []) {
  return asArray(form?.hoarding_ids).reduce((sum, hId) => {
    const hoarding = hoardings.find((item) => item.id === hId);
    const rawCharge = form?.contract_amounts?.[hId];
    const charge = rawCharge != null ? chargeFor(rawCharge) : chargeFor({ display: Number(hoarding?.monthly_price || 0) });
    return sum + campaignChargeTotal(charge, form?.start_date, form?.end_date, form?.is_open_ended);
  }, 0);
}

export function updateCharge(amounts, hId, field, value) {
  const current = chargeFor(amounts[hId]);
  const stringFields = new Set(["display_margin_type", "printing_margin_type", "installation_margin_type"]);
  const nextValue = stringFields.has(field) ? value : parseFloat(value) || 0;
  return { ...amounts, [hId]: { ...current, [field]: nextValue } };
}

export function latestBusinessMargins(margins = [], asOf = "") {
  const date = asOf || new Date().toISOString().slice(0, 10);
  const latest = {};
  margins
    .filter((m) => !m.effective_from || m.effective_from <= date)
    .sort((a, b) => String(b.effective_from || "").localeCompare(String(a.effective_from || "")))
    .forEach((m) => {
      if (!latest[m.charge_type]) latest[m.charge_type] = m;
    });
  return latest;
}

export function defaultChargeForHoarding(hoarding, businessMargins = {}, existing = null) {
  const charge = chargeFor(existing || {});
  const withDefaults = {
    ...charge,
    display: charge.display || Number(hoarding?.monthly_price || 0),
    printing: charge.printing || Number(hoarding?.default_printing_charge || 0),
    installation: charge.installation || Number(hoarding?.default_installation_charge || 0),
  };
  if (withDefaults.display > 0 && !withDefaults.display_margin_type && hoarding?.default_display_margin_type) {
    withDefaults.display_margin_type = hoarding.default_display_margin_type;
    withDefaults.display_margin_value = Number(hoarding.default_display_margin_value || 0);
  }
  if (withDefaults.printing > 0 && !withDefaults.printing_margin_type && businessMargins.printing) {
    withDefaults.printing_margin_type = businessMargins.printing.margin_type;
    withDefaults.printing_margin_value = Number(businessMargins.printing.margin_value || 0);
  }
  if (withDefaults.installation > 0 && !withDefaults.installation_margin_type && businessMargins.installation) {
    withDefaults.installation_margin_type = businessMargins.installation.margin_type;
    withDefaults.installation_margin_value = Number(businessMargins.installation.margin_value || 0);
  }
  return withDefaults;
}

export function marginProfit(amount, type, value) {
  const charge = Number(amount || 0);
  const margin = Number(value || 0);
  if (charge <= 0 || !type || margin <= 0) return 0;
  if (type === "percent") return Math.min(charge, (charge * margin) / 100);
  if (type === "amount") return Math.min(charge, margin);
  return 0;
}

export function validateCampaignMargins(form) {
  for (const hId of asArray(form.hoarding_ids)) {
    const charge = chargeFor(form.contract_amounts?.[hId]);
    const checks = [
      ["Display", charge.display, charge.display_margin_type, charge.display_margin_value],
      ["Printing", charge.printing, charge.printing_margin_type, charge.printing_margin_value],
      ["Installation", charge.installation, charge.installation_margin_type, charge.installation_margin_value],
    ];
    for (const [label, amount, type, value] of checks) {
      if (!type || Number(value || 0) === 0) continue;
      if (type === "percent" && Number(value || 0) > 100) return `${label} margin cannot be more than 100%.`;
      if (type === "amount" && Number(value || 0) > Number(amount || 0)) return `${label} margin amount cannot be greater than the client charge.`;
    }
  }
  return "";
}

export function normalizeCampaign(campaign) {
  return {
    ...campaign,
    hoarding_ids: asArray(campaign?.hoarding_ids),
    assigned_hoardings: asArray(campaign?.assigned_hoardings),
    contract_amounts: campaign?.contract_amounts || {},
    revenue: campaign?.revenue || 0,
    hoarding_count: campaign?.hoarding_count || 0,
  };
}

export function defaultSort(arr) {
  return arr.slice().sort((a, b) => {
    const so = (STATUS_ORDER[a.status] ?? 5) - (STATUS_ORDER[b.status] ?? 5);
    if (so !== 0) return so;
    switch (a.status) {
      case "Expiring Soon":
      case "Active":
        return new Date(a.end_date) - new Date(b.end_date);
      case "Upcoming":
        return new Date(a.start_date) - new Date(b.start_date);
      case "Completed":
        return new Date(b.end_date) - new Date(a.end_date);
      default:
        return 0;
    }
  });
}

export function expiryClass(c) {
  if (c.status === "Completed") return "text-slate-400";
  const days = c.remaining_days;
  if (days == null) return "text-slate-500";
  if (days <= 3) return "text-blue-600 font-bold";
  if (days <= 7) return "text-amber-600 font-bold";
  if (days <= 14) return "text-amber-500 font-semibold";
  return "text-slate-700 font-semibold";
}
